import { useState } from 'react';

const faqs = [
  {
    question: 'How often should I have my HVAC system serviced?',
    answer: 'We recommend scheduling maintenance twice a year — once in the spring before cooling season and once in the fall before heating season. Regular tune-ups keep your system running efficiently, extend its lifespan, and help prevent unexpected breakdowns.'
  },
  {
    question: 'How long does an AC installation take?',
    answer: 'Most standard AC installations are completed in 4–8 hours. The exact time depends on the size of your home, the type of system being installed, and whether any ductwork modifications are needed. We always aim to have your new system up and running the same day.'
  },
  {
    question: 'What brands of HVAC equipment do you work with?',
    answer: 'Our technicians are trained to service and install all major HVAC brands, including Trane, Carrier, Lennox, Rheem, Goodman, American Standard, and more. We can recommend the best brand and model for your home and budget.'
  },
  {
    question: 'Do you offer emergency or same-day service?',
    answer: 'Yes! We understand that AC problems in Southwest Florida can\'t wait. We offer same-day service and emergency appointments to get your home comfortable as quickly as possible. Just give us a call and we\'ll do our best to get a technician out to you right away.'
  },
  {
    question: 'How do I know if I should repair or replace my AC unit?',
    answer: 'A good rule of thumb is the "5,000 rule" — multiply the age of your unit by the repair cost. If the result exceeds $5,000, replacement is usually the smarter investment. We\'ll always give you an honest assessment and never push you toward a replacement you don\'t need.'
  },
  {
    question: 'What is included in a maintenance tune-up?',
    answer: 'Our comprehensive tune-up includes inspecting and cleaning the condenser and evaporator coils, checking refrigerant levels, testing electrical components, lubricating moving parts, inspecting the thermostat, clearing the condensate drain line, and checking overall system performance.'
  },
  {
    question: 'Why is my energy bill so high?',
    answer: 'High energy bills are often caused by a dirty air filter, low refrigerant, a failing compressor, or an aging system that\'s lost efficiency. A quick maintenance visit can usually identify the culprit and get your system back to peak performance, saving you money every month.'
  },
  {
    question: 'Are you licensed and insured?',
    answer: 'Absolutely. Doolittle Air is fully licensed, bonded, and insured in the state of Florida. You can have complete peace of mind knowing every job is done safely, professionally, and to code.'
  }
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-24 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-orange-500 font-semibold text-sm uppercase tracking-wider mb-3">
            GOT QUESTIONS?
          </p>
          <h2 className="text-5xl font-bold text-slate-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Here are answers to the questions we hear most often. Don't see yours? Give us a call — we're always happy to help.
          </p>
        </div>

        {/* FAQ List */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className={`rounded-2xl border transition-all duration-300 overflow-hidden ${
                openIndex === index
                  ? 'border-orange-400 shadow-md'
                  : 'border-gray-200 hover:border-orange-300'
              }`}
            >
              <button
                onClick={() => toggle(index)}
                className="w-full flex items-center justify-between gap-4 px-8 py-6 text-left cursor-pointer bg-white hover:bg-orange-50 transition-colors duration-200"
              >
                <span className="text-lg font-semibold text-slate-900 leading-snug">
                  {faq.question}
                </span>
                <span className={`w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full transition-all duration-300 ${
                  openIndex === index ? 'bg-orange-500 text-white rotate-45' : 'bg-gray-100 text-gray-500'
                }`}>
                  <i className="ri-add-line text-lg"></i>
                </span>
              </button>

              <div
                className={`transition-all duration-300 ease-in-out ${
                  openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                } overflow-hidden`}
              >
                <div className="px-8 pb-6 text-gray-600 text-base leading-relaxed border-t border-gray-100 pt-4">
                  {faq.answer}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-14 text-center bg-orange-50 rounded-2xl p-10 border border-orange-100">
          <div className="w-14 h-14 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-customer-service-2-fill text-2xl text-white"></i>
          </div>
          <h3 className="text-2xl font-bold text-slate-900 mb-2">Still have questions?</h3>
          <p className="text-gray-600 mb-6">Our friendly team is just a phone call away. We're here to help!</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+19412233052"
              className="inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-4 rounded-full transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill text-lg"></i>
              Call (941) 223-3052
            </a>
            <a
              href="#contact"
              className="inline-flex items-center justify-center gap-2 bg-white border-2 border-orange-500 text-orange-500 hover:bg-orange-50 font-bold px-8 py-4 rounded-full transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-send-line text-lg"></i>
              Send a Message
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
